import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { useGroupByTableLogic } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.logic';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.module.scss';
import { useGetPivotTableProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.provider';
import { PivotFilters } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/Filters/PivotFilters';
import { GroupedByTable } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable';
import { initialState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/components/GroupByTable/GroupedByTable.utils';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Alert } from 'components/DesignSystem/Alert';

export const PivotBoard = ({
    projectId,
    contractId,
    latestVersionId,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    globalFilterData,
    title: propTitle,
}: IComponentProps) => {
    const { componentKey, id, endpoints, name: configTitle } = useConfigurationStore().activitiesPivotTableView;
    const titleToUse = propTitle || configTitle;

    const { api, ui } = useGetPivotTableProvider({
        globalFilterData,
        latestVersionId,
        contractId,
        projectId,
        endpoints,
        widgetViewMode,
        localFilters,
        widgetId,
    });

    const { logic } = useGroupByTableLogic({ groupsData: api.getPivotTable.instance.data?.groupList ?? [], ui, api });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: logic.groupedByTableColumns,
        widgetViewMode,
    });

    const getPivotTableEndpoint = endpoints['getPivotTable'];

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: titleToUse,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getPivotTable.payload,
        widgetEndpoint: getPivotTableEndpoint,
    });

    if (
        (WidgetViewModeUtils.isPageView(widgetViewMode) && ui.state.isPivotTableViewSelected) ||
        WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
    ) {
        return (
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        showPostfixInEditMode={true}
                        postfix={`(${logic.groupedByTableDataTree.length ?? 0})`}
                    />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <PivotFilters key={'PivotTableFilters'} ui={ui} />
                    ),
                    WidgetViewModeUtils.isPageView(widgetViewMode) && (
                        <Separator key={'separator'} className={classes.separator} />
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        apiPayload={api.getPivotTable.payload}
                        widgetEndpoint={api.getPivotTable.endpoint}
                    />,

                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'ExportDropdown'}
                            title={titleToUse}
                            subTitle={subTitle}
                            handleAPICsvExport={
                                logic.handleAPICsvExportPrivilege
                                    ? logic.getExportPivotTableToCsvHandler(titleToUse, subTitle)
                                    : undefined
                            }
                        />
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            key={'PivotTableColumnsManager'}
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={logic.groupedByTableColumns}
                            onChangeOrder={table.setColumnOrder}
                            onChangeVisibility={table.setColumnVisibility}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={titleToUse}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                ]}
            >
                {api.getPivotTable.instance.data?.totalGroupsSizeWarning && (
                    <Alert severity={'warning'} className={classes.alertContainer}>
                        {api.getPivotTable.instance.data?.totalGroupsSizeWarning}
                    </Alert>
                )}
                <TableContainer>
                    <GroupedByTable
                        TableData={logic.groupedByTableDataTree}
                        table={table}
                        isLoading={
                            api.getPivotTable.instance.isFetching || api.exportPivotTableToCsv.instance.isLoading
                        }
                        tableColumns={logic.groupedByTableColumns}
                        widgetViewMode={widgetViewMode}
                    />
                </TableContainer>
            </WidgetWithTitle>
        );
    } else {
        // should display pivot table only in project activities page and when Pivot selected
        return null;
    }
};
